import React, { useState, useEffect, useCallback } from "react";
import { updatePromotion } from "../../../server/PriceList";
import { setPreload } from "../../../server/CartsOpen";
import showAlert from "../../../server/ShowAlert";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { showAlertWithConfirmCancel } from "../../../server/ShowAlert";
import { Trash2, Calendar } from "lucide-react";
import { getSales } from "../../../server/Product";
import LoadingStatus from "../../ui/LoadingStatus";
import {
  groupPromotionsByPromoId,
  formatPromotionBadge,
  getPackageAdminBadgeLabel,
  getPromotionApiErrorMessage,
  isPackagePromo,
} from "./promotionUtils";
import PromoCardProducts, {
  PromoProductsModal,
  getPromoGtins,
  getPromoCardTitle,
} from "./PromoCardProducts";
import "./Sale.scss";

const formatDate = (date) => {
  const d = new Date(date);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getPromoStatus = (item) =>
  String(item?.status ?? item?.Status ?? "").toUpperCase();

const SalesManagement = () => {
  const dispatch = useDispatch();
  const [salesList, setSalesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [_partialSalesList, setPartialSalesList] = useState([]);
  const [productsModalPromo, setProductsModalPromo] = useState(null);
  const stationId = useSelector((state) => state.user.stationId);
  const products = useSelector(
    (state) => state.displayProduct.itemsShow.products,
  );

  const getSalesList = useCallback(() => {
    dispatch(setPreload(true));
    setLoading(true);
    dispatch(getSales(stationId, false))
      .then((res) => {
        if (res.data) {
          setSalesList(groupPromotionsByPromoId(res.data));
        }
      })
      .catch((err) => {
        console.error("Error fetching sales:", err);
        showAlert({ msg: "שגיאה בטעינת המבצעים" });
      })
      .finally(() => {
        setLoading(false);
        dispatch(setPreload(false));
      });
  }, [dispatch, stationId]);

  useEffect(() => {
    if (stationId) getSalesList();
    else setLoading(false);
  }, [stationId, getSalesList]);

  const handleDeleteSale = (saleId) => {
    showAlertWithConfirmCancel({
      msgTitle: "האם את/ה בטוח/ה שברצונך למחוק את המבצע?",
      confirmText: "מחק",
      cancelText: "ביטול",
      onConfirm: () => {
        updatePromotion(saleId, { status: "DISABLED" })
          .then(() => {
            showAlert({ msgTitle: "המבצע נמחק בהצלחה", msgType: "success" });
            setSalesList((prev) =>
              prev.filter(
                (item) => item.Id !== saleId && item.promoId !== saleId,
              ),
            );
            setPartialSalesList((prev) =>
              prev.filter(
                (item) => item.Id !== saleId && item.promoId !== saleId,
              ),
            );
          })
          .catch((error) => {
            const message = getPromotionApiErrorMessage(
              error,
              "הייתה בעיה במחיקת המבצע",
            );
            showAlert({
              msgTitle: "לא ניתן למחוק את המבצע",
              msgText: message,
              msgType: "error",
            });
          });
      },
    });
  };

  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const isPromoActive = (item) => {
    // DISABLED stays inactive even if dates still cover today.
    if (getPromoStatus(item) === "DISABLED") return false;
    const today = normalizeDate(new Date());
    const start = normalizeDate(new Date(item.startDate));
    const end = normalizeDate(new Date(item.endDate));
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }
    return start <= today && end >= today;
  };

  const renderSaleCard = (item, { editable }) => {
    const badge = formatPromotionBadge(item);
    const gtins = getPromoGtins(item);
    const cardTitle = getPromoCardTitle(item, products);
    const promoLabel = isPackagePromo(item)
      ? getPackageAdminBadgeLabel(item)
      : badge?.text;

    const cardBody = (
      <>
        {promoLabel ? (
          <span className="promo-card__badge">{promoLabel}</span>
        ) : null}

        <div className="promo-card__title">{cardTitle}</div>

        <div className="promo-card__dates">
          <Calendar aria-hidden size={13} strokeWidth={2} />
          {formatDate(item.startDate)} – {formatDate(item.endDate)}
        </div>
      </>
    );

    return (
      <div
        className={`promo-card${editable ? "" : " promo-card--readonly"}`}
        key={item.promoId}
      >
        <button
          type="button"
          className="promo-card__delete"
          onClick={() => handleDeleteSale(item.promoId)}
          aria-label="מחיקת מבצע"
          title="מחיקת מבצע"
        >
          <Trash2 aria-hidden size={16} strokeWidth={2} />
        </button>

        {editable ? (
          <NavLink
            to={"/sale-page"}
            state={{
              saleData: item,
              product: products.find((p) => p.gtin === gtins[0]),
            }}
            className="promo-card__link"
          >
            {cardBody}
          </NavLink>
        ) : (
          <div className="promo-card__link promo-card__link--static">
            {cardBody}
            <p className="promo-card__readonly-hint">
              לא ניתן לערוך או להוסיף מוצרים למבצע לא פעיל
            </p>
          </div>
        )}

        <PromoCardProducts
          gtins={gtins}
          products={products}
          onShowAll={() => setProductsModalPromo(item)}
        />
      </div>
    );
  };

  // Show every promotion from the server (after promoId grouping).
  const activeSales = salesList.filter(isPromoActive);
  const inactiveSales = salesList.filter((item) => !isPromoActive(item));

  return (
    <div className="promotions-page admin-page admin-page--fill">
      <PromoProductsModal
        promo={productsModalPromo}
        products={products}
        onClose={() => setProductsModalPromo(null)}
      />

      <header className="admin-page__header">
        <div className="admin-page__header-start">
          <h1 className="title-role">ניהול מבצעים</h1>
        </div>
        <div className="admin-page__header-end">
          <span className="admin-count-badge">
            {salesList.length} מבצעים בסה״כ
          </span>
        </div>
      </header>

      <div className="admin-page__body">
        {loading ? (
          <LoadingStatus />
        ) : (
          <>
            <section className="promotions-section">
              <div className="promotions-section__title">
                <span className="live-dot" aria-hidden />
                מבצעים פעילים
                <span className="promotions-section__badge">
                  {activeSales.length}
                </span>
              </div>
              {activeSales.length ? (
                <div className="promotions-grid">
                  {activeSales.map((item) =>
                    renderSaleCard(item, { editable: true }),
                  )}
                </div>
              ) : (
                <div className="promotions-empty">אין מבצעים פעילים כרגע</div>
              )}
            </section>

            <section className="promotions-section">
              <div className="promotions-section__title">
                מבצעים לא פעילים
                <span className="promotions-section__badge">
                  {inactiveSales.length}
                </span>
              </div>
              {inactiveSales.length ? (
                <div className="promotions-grid">
                  {inactiveSales.map((item) =>
                    renderSaleCard(item, { editable: false }),
                  )}
                </div>
              ) : (
                <div className="promotions-empty">אין מבצעים לא פעילים</div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default SalesManagement;
